import { createFileRoute, notFound } from "@tanstack/react-router";
import { TopicHubPage } from "@/pages/topics/TopicPages";
import { breadcrumbJsonLd } from "@/config/seoConfig";
import { seoHead, SITE_BASE_URL } from "@/lib/seo/head";
import { getPublicCourses, type PublicCourse } from "@/lib/publicCourses.functions";

export const Route = createFileRoute("/topics/$slug")({
  // Topic data links every guide and article, so it is imported on demand
  // instead of riding in every page's startup code.
  loader: async ({ params }) => {
    const { TOPIC_BY_SLUG, resolveTopic } = await import("@/content/topics");
    const topic = TOPIC_BY_SLUG[params.slug];
    if (!topic) throw notFound();
    let all: PublicCourse[] = [];
    try {
      all = (await getPublicCourses()).flatMap((l) => l.courses);
    } catch {
      all = [];
    }
    // Keep config order; unknown slugs are dropped, never guessed.
    const courses = topic.courses
      .map((s) => all.find((c) => c.slug === s))
      .filter((c): c is PublicCourse => Boolean(c) && (c!.status === "published" || c!.status === "coming_soon"));
    const r = resolveTopic(topic);
    const items = [
      ...r.guides.map((g) => ({ name: g.label, url: `${SITE_BASE_URL}${g.href}` })),
      ...courses
        .filter((c) => c.status === "published")
        .map((c) => ({ name: c.title, url: `${SITE_BASE_URL}/courses/${c.slug}` })),
      ...r.tools.map((t) => ({ name: t.label, url: `${SITE_BASE_URL}${t.href}` })),
    ];
    return { topic, courses, items };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return seoHead({ title: "Topic not found", noindex: true });
    const { topic, items } = loaderData;
    const url = `${SITE_BASE_URL}/topics/${topic.slug}`;
    return seoHead({
      title: topic.title,
      description: topic.description,
      canonical: `/topics/${topic.slug}`,
      pageType: "CollectionPage",
      jsonLd: [
        breadcrumbJsonLd([
          { name: "Home", url: "/" },
          { name: "Topics", url: "/topics" },
          { name: topic.title, url: `/topics/${topic.slug}` },
        ]),
        items.length
          ? {
              "@type": "ItemList",
              "@id": `${url}#resources`,
              itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, url: it.url })),
            }
          : null,
      ],
    });
  },
  component: TopicHub,
});

function TopicHub() {
  const { topic, courses } = Route.useLoaderData();
  return <TopicHubPage topic={topic} courses={courses} />;
}
